import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isNil, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { SegmentById } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { setDynamicSegmentMainSegmentInitialData } from 'store/slices/dynamicSegment';
import { selectBaseQueryConfigurations } from 'store/slices/queryConfigurations';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useToaster from 'hooks/UseToaster';
//Import Components
import StaticSegmentInfo from './StaticSegmentInfo';
import ActionLink from 'components/common/ActionLink';
import CompositeSegmentInfo from './CompositeSegmentInfo';
import SegmentInfoModalBody from './SegmentInfoModalBody';
import DynamicSegmentInfo from 'components/DynamicSegment/DynamicSegmentInfo';
//Import UI components
import { Modal, Button, Tooltip } from '@geneui/components';
// Import Services
import {
    getSegmentClientCount,
    getCampaignsBySegment,
    getStaticSegmentTop200Client,
    getSegmentById,
    getNonCustomerSegmentPreview,
} from './service';
import { getQueryConfigurationsList } from 'services/segment';
// Import Constants
import { SegmentTypesLabels, ModalsActions, AlertTypes, l, SegmentObjectType, ActionsIcons } from 'constants/common';
import { RoutesList } from 'routes';
// Import Version History
import {
    useVersionHistoryContext,
    VersionHistoryIconWithPopover,
    VersionHistoryProviderHOC,
    VersionHistorySelectedVersionLabel,
} from 'components/VersionHistory';
// Import Enums
import { ObjectTypeEnum } from 'enums';
// Import Styles
import 'assets/scss/segmentModal.scss';

const { error } = AlertTypes;

const SegmentModal = (props) => {
    const {
        segmentId,
        segmentType,
        isPredefined,
        handleClose,
        isVisible,
        fromCampaignModal,
        targetObject,
        handleInfoModalActionClick,
        withTitleActions,
        withActionButtons,
        predefinedKeyValue,
    } = props;
    const { t } = useTranslation();
    const { getRoutePath } = useGoToRoute();
    const { showToaster } = useToaster();
    const dispatch = useAppDispatch();
    const queryConfigurations = useAppSelector(selectBaseQueryConfigurations);
    const [segmentFiltersData, setSegmentFiltersData] = useState(null);

    const { pointInTime, isHistoryMode } = useVersionHistoryContext();

    const errorHandler = (alertMessage) => showToaster(error, alertMessage);
    const [segmentInfoType, setSegmentInfoType] = useState(segmentType);

    const [isLoading, setIsLoading] = useState(false);

    const [segmentInfo, setSegmentInfo] = useState<Nullable<SegmentById>>(null);

    const [usedInCampaigns, setUsedInCampaigns] = useState(null);

    const [staticSegmentInfo, setStaticSegmentInfo] = useState(null);

    const isStatic = segmentInfoType === SegmentTypesLabels.Static;

    const isVisibleEdit = () => segmentInfo && !isStatic && !isPredefined && !isHistoryMode;
    const isVisibleCreateReport = () => segmentInfo && targetObject !== SegmentObjectType.NonCustomer && !isHistoryMode;
    const isVisibleClone = () => segmentInfo && !isStatic && !isHistoryMode;
    const isVisibleVersionHistory = () => segmentInfoType !== SegmentTypesLabels.Static;

    const convertToReportPath = useMemo(() => {
        return getRoutePath(RoutesList.CONVERT_TO_REPORT, { segmentId });
    }, [getRoutePath, segmentId]);

    const closeConvertToReportModal = () => {
        handleClose(ModalsActions.CONVERT_TO_REPORT);
    };

    const clonePath = useMemo(() => {
        if (segmentInfoType === SegmentTypesLabels.Dynamic) {
            return getRoutePath(RoutesList.SEGMENT_DYNAMIC_CLONE, {
                segmentId,
            });
        } else {
            return getRoutePath(RoutesList.SEGMENT_COMPOSITE_CLONE, { segmentId });
        }
    }, [segmentInfoType, getRoutePath, segmentId]);

    const closeCloneModal = () => {
        handleClose(ModalsActions.CLONE);
    };

    const closeEditModal = () => {
        handleClose(ModalsActions.EDIT);
    };

    const editSegmentPath = useMemo(() => {
        if (segmentInfoType === SegmentTypesLabels.Dynamic) {
            return getRoutePath(RoutesList.SEGMENT_DYNAMIC_EDIT, { segmentId });
        } else {
            return getRoutePath(RoutesList.SEGMENT_COMPOSITE_EDIT, { segmentId });
        }
    }, [segmentInfoType, getRoutePath, segmentId]);

    const getTitleKey = () => {
        return isPredefined ? l['PredefinedSegmentInfo'] : l[`${SegmentTypesLabels[segmentInfoType]}SegmentInfo`];
    };

    const {
        getSegmentClientCountRequest,
        getCampaignsBySegmentRequest,
        getSegmentByIdRequest,
        getStaticSegmentTop200ClientRequest,
        getQueryConfigurationsListRequest,
        getNonCustomerSegmentPreviewRequest,
    } = useMemo(
        () => ({
            getSegmentByIdRequest: getSegmentById(),
            getSegmentClientCountRequest: getSegmentClientCount(),
            getCampaignsBySegmentRequest: getCampaignsBySegment(),
            getStaticSegmentTop200ClientRequest: getStaticSegmentTop200Client(),
            getQueryConfigurationsListRequest: getQueryConfigurationsList(),
            getNonCustomerSegmentPreviewRequest: getNonCustomerSegmentPreview(),
        }),
        [],
    );

    const renderSegmentInfo = () => {
        switch (segmentInfoType) {
            case SegmentTypesLabels.Static:
                return <StaticSegmentInfo staticSegmentInfoData={staticSegmentInfo} />;
            case SegmentTypesLabels.Dynamic:
                return <DynamicSegmentInfo segmentFiltersData={segmentFiltersData} />;
            case SegmentTypesLabels.Composite:
                return <CompositeSegmentInfo compositeConfiguration={segmentInfo?.CompositeConfiguration} />;
            default:
                return '';
        }
    };

    const requestSegmentInfo = () => {
        if (targetObject === SegmentObjectType.NonCustomer) {
            getNonCustomerSegmentPreviewRequest.request(segmentId, setStaticSegmentInfo, errorHandler);
            return;
        }
        switch (segmentInfoType) {
            case SegmentTypesLabels.Static:
                getStaticSegmentTop200ClientRequest.request(
                    segmentId,
                    (data) => setStaticSegmentInfo({ ...data, SegmentId: segmentId, Name: segmentInfo?.Name }),
                    errorHandler,
                );
                return;
            case SegmentTypesLabels.Dynamic:
                getQueryConfigurationsListRequest.request(
                    dispatch,
                    queryConfigurations,
                    segmentInfo,
                    {
                        shouldNotDispatch: true,
                        setData: (data) => {
                            dispatch(setDynamicSegmentMainSegmentInitialData(data));
                            setSegmentFiltersData(data);
                        },
                    },
                    t,
                );
                return;
            default:
                return;
        }
    };

    const getSegmentInfoCallBack = (data: SegmentById) => {
        setSegmentInfo(data);
        setIsLoading(false);
        if (isNil(segmentInfoType)) {
            setSegmentInfoType(data.Type);
        }
    };

    const getSegmentInfo = () => {
        setIsLoading(true);
        getSegmentByIdRequest.request(segmentId, getSegmentInfoCallBack, errorHandler, pointInTime);
    };

    const isReadyToRequest = !isNil(segmentInfo) && !isNil(segmentInfoType);

    useEffect(() => {
        if (isReadyToRequest) {
            requestSegmentInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReadyToRequest, pointInTime, segmentInfo]);

    useEffect(() => {
        getSegmentInfo();

        if (!isPredefined) {
            getCampaignsBySegmentRequest.request(segmentId, setUsedInCampaigns, errorHandler, pointInTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segmentId, pointInTime]);

    const cleanUp = () => {
        return () => {
            setSegmentInfo(null);
            setUsedInCampaigns(null);
            setIsLoading(true);
            getSegmentByIdRequest.cancel('SegmentModal:getSegment');
            getSegmentClientCountRequest.cancel('SegmentModal:getSegmentClientCount');
            getCampaignsBySegmentRequest.cancel('SegmentModal:getCampaignsBySegment');
            getStaticSegmentTop200ClientRequest.cancel('SegmentModal:getStaticSegmentTop200Client');
            getQueryConfigurationsListRequest.cancel('SegmentModal:getQueryConfigurationsList');
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <>
            {fromCampaignModal ? (
                <SegmentInfoModalBody
                    isLoading={isLoading}
                    segmentInfo={segmentInfo}
                    segmentInfoType={segmentInfoType}
                    isPredefined={isPredefined}
                    renderSegmentInfo={renderSegmentInfo}
                    usedInCampaigns={usedInCampaigns}
                    handleInfoModalActionClick={handleInfoModalActionClick}
                    withActionButtons={withActionButtons && !isHistoryMode}
                />
            ) : (
                <Modal
                    closeOnClickOutside={true}
                    background="dark-background"
                    withPortal={true}
                    position="center"
                    visible={isVisible}
                    closable={false}
                    onCancel={() => handleClose(ModalsActions.CLOSE)}
                    className="crm-object-list-modal"
                    title={
                        <div className="crm-object-list-modal-head">
                            <span className="crm-object-list-modal-h-text">{t(getTitleKey())}</span>
                            {withTitleActions && (
                                <div className="crm-object-list-modal-h-icons">
                                    {isVisibleVersionHistory() && (
                                        <>
                                            <VersionHistorySelectedVersionLabel
                                                objectId={segmentId}
                                                objectTypeId={ObjectTypeEnum.Segment}
                                            />
                                            <VersionHistoryIconWithPopover
                                                objectId={segmentId}
                                                objectTypeId={ObjectTypeEnum.Segment}
                                            />
                                        </>
                                    )}
                                    {isVisibleEdit() && (
                                        <Tooltip text={t(l.EditSegment)}>
                                            <ActionLink
                                                icon={ActionsIcons.Edit}
                                                appearance="minimal"
                                                color="primary"
                                                onRedirect={closeEditModal}
                                                redirectPath={editSegmentPath}
                                            />
                                        </Tooltip>
                                    )}
                                    {isVisibleCreateReport() && (
                                        <Tooltip text={t(l.CreateReport)}>
                                            <ActionLink
                                                icon={ActionsIcons.ConvertToReport}
                                                appearance="minimal"
                                                color="primary"
                                                onRedirect={closeConvertToReportModal}
                                                redirectPath={convertToReportPath}
                                            />
                                        </Tooltip>
                                    )}
                                    {isVisibleClone() && (
                                        <Tooltip text={t(l.CloneSegment)}>
                                            <ActionLink
                                                icon={ActionsIcons.Clone}
                                                appearance="minimal"
                                                color="primary"
                                                onRedirect={closeCloneModal}
                                                redirectPath={clonePath}
                                            />
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                    footer={
                        <Button color="primary" onClick={() => handleClose(ModalsActions.CLOSE)}>
                            {t(l.Close)}
                        </Button>
                    }
                >
                    <SegmentInfoModalBody
                        isLoading={isLoading}
                        segmentInfo={segmentInfo}
                        segmentInfoType={segmentInfoType}
                        isPredefined={isPredefined}
                        renderSegmentInfo={renderSegmentInfo}
                        usedInCampaigns={usedInCampaigns}
                        predefinedKeyValue={predefinedKeyValue}
                    />
                </Modal>
            )}
        </>
    );
};

SegmentModal.propTypes = {
    segmentId: PropTypes.number.isRequired,
    segmentType: PropTypes.number,
    isVisible: PropTypes.bool,
    isPredefined: PropTypes.bool,
    handleClose: PropTypes.func,
    fromCampaignModal: PropTypes.bool,
    targetObject: PropTypes.number.isRequired,
    handleInfoModalActionClick: PropTypes.func,
    withTitleActions: PropTypes.bool,
    withActionButtons: PropTypes.bool,
};

SegmentModal.defaultProps = {
    handleClose: noop,
    isVisible: false,
    isPredefined: false,
    fromCampaignModal: false,
    segmentType: null,
    withTitleActions: true,
    withActionButtons: true,
};

export default memo(VersionHistoryProviderHOC(SegmentModal));
